'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { downloadBlob } from '../lib/api/dashboard';
import { resolveAnalyticalYearPeriod } from '../lib/api/analytical-periods';
import {
  exportBusinessMedicsRevenue,
  getBusinessMedicsRevenue,
  type BusinessMedicsRevenueQuery,
  type BusinessMedicsRevenueReport,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';

const TODAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date());
type MedicsRevenueView = BusinessMedicsRevenueQuery['view'] | 'YTD' | 'FY_YTD';

export function BusinessMedicsRevenueTab({
  active,
  token,
  canExport,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  canExport: boolean;
  onTopBarActionsChange?: (actions: ReactNode | null) => void;
}) {
  const [view, setView] = useState<MedicsRevenueView>('MTD');
  const [asOf, setAsOf] = useState(TODAY);
  const [from, setFrom] = useState(TODAY.slice(0, 8) + '01');
  const [to, setTo] = useState(TODAY);
  const [center, setCenter] = useState('ALL');
  const [report, setReport] = useState<BusinessMedicsRevenueReport | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; title: string; message: string } | null>(null);

  const query = useMemo<BusinessMedicsRevenueQuery>(() => ({
    view: view === 'YTD' || view === 'FY_YTD' ? 'DATE_RANGE' : view,
    asOf,
    center,
    ...(view === 'DATE_RANGE' || view === 'YTD' || view === 'FY_YTD' ? { from, to } : {}),
  }), [asOf, center, from, to, view]);

  const changeView = async (next: MedicsRevenueView) => {
    if (next === 'YTD' || next === 'FY_YTD') {
      try {
        const range = await resolveAnalyticalYearPeriod(next, token, asOf);
        setFrom(range.from);
        setTo(range.to);
      } catch (caught) {
        setAlert({ type: 'error', title: 'Reporting period unavailable', message: message(caught) });
        return;
      }
    }
    setView(next);
  };

  const changeAsOf = async (nextAsOf: string) => {
    if (view === 'YTD' || view === 'FY_YTD') {
      try {
        const range = await resolveAnalyticalYearPeriod(view, token, nextAsOf);
        setFrom(range.from);
        setTo(range.to);
      } catch (caught) {
        setAlert({ type: 'error', title: 'Reporting period unavailable', message: message(caught) });
        return;
      }
    }
    setAsOf(nextAsOf);
  };

  const load = useCallback(async () => {
    setBusy('Generating centre-scoped Medics revenue report...');
    setError(null);
    try {
      setReport(await getBusinessMedicsRevenue(query, token));
    } catch (loadError) {
      setReport(null);
      setError(message(loadError));
    } finally {
      setBusy('');
    }
  }, [query, token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const exportReport = useCallback(async () => {
    setBusy('Preparing governed Excel report...');
    try {
      const result = await exportBusinessMedicsRevenue(query, token);
      downloadBlob(result.blob, result.fileName || `Ferty9_Medics_Revenue_${report?.period.from}_${report?.period.to}.xlsx`);
    } catch (exportError) {
      setAlert({ type: 'error', title: 'Export failed', message: message(exportError) });
    } finally {
      setBusy('');
    }
  }, [query, report?.period.from, report?.period.to, token]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;

    if (!canExport) {
      onTopBarActionsChange(null);
      return () => onTopBarActionsChange(null);
    }

    onTopBarActionsChange(
      <button
        className="btn btn-primary compact-btn"
        type="button"
        onClick={() => void exportReport()}
        disabled={!report || Boolean(busy)}
      >
        <DownloadIcon />
        {busy ? 'Preparing…' : 'Export Excel'}
      </button>,
    );

    return () => onTopBarActionsChange(null);
  }, [active, busy, canExport, exportReport, onTopBarActionsChange, report]);

  if (!active) return null;

  const centers = report?.centers ?? [];
  const main = report?.totals.bySource.find((row) => row.sourceKey === 'MAIN');
  const pharmacy = report?.totals.bySource.find((row) => row.sourceKey === 'PHARMACY');
  const trend = view === 'MONTHLY_TREND' ? report?.monthlyTrend ?? [] : report?.dailyTrend ?? [];

  return (
    <section className="tab-content business-medics-report-page" id="tab-business-medics-revenue" aria-labelledby="page-title" aria-busy={Boolean(busy)}>
      {busy && <Loader message={busy} />}
      <form className="card enterprise-filter-card medics-revenue-filter-card" onSubmit={(event) => { event.preventDefault(); void load(); }}>
        <div className="enterprise-filter-grid medics-revenue-filter-grid">
        <label className="enterprise-field"><span>View</span>
          <select value={view} onChange={(event) => void changeView(event.target.value as MedicsRevenueView)}>
            <option value="DAILY">Daily</option><option value="MTD">Month to date</option><option value="COMPLETED_MONTH">Completed month</option><option value="YTD">YTD (Year to Date)</option><option value="FY_YTD">FY-YTD (Financial Year to Date)</option><option value="DATE_RANGE">Date range</option><option value="MONTHLY_TREND">Monthly trend</option>
          </select>
        </label>
        <label className="enterprise-field"><span>As-of date</span><input type="date" value={asOf} max={TODAY} onChange={(event) => void changeAsOf(event.target.value)} /></label>
        {view === 'DATE_RANGE' && <><label className="enterprise-field"><span>From</span><input type="date" value={from} max={TODAY} onChange={(event) => setFrom(event.target.value)} /></label><label className="enterprise-field"><span>To</span><input type="date" value={to} min={from} max={TODAY} onChange={(event) => setTo(event.target.value)} /></label></>}
        <label className="enterprise-field"><span>Centre</span>
          <select value={center} onChange={(event) => setCenter(event.target.value)}>
            <option value="ALL">All permitted centres</option>
            {centers.map((row) => <option key={row.centerCode} value={row.centerCode}>{row.displayName}</option>)}
          </select>
        </label>
        <div className="medics-revenue-filter-actions">
          <button className="btn btn-outline compact-btn filter-action" type="button" onClick={() => { setView('MTD'); setAsOf(TODAY); setFrom(`${TODAY.slice(0, 8)}01`); setTo(TODAY); setCenter('ALL'); }} disabled={Boolean(busy)}>Clear Filters</button>
          <button className="btn btn-primary compact-btn filter-action" type="submit" disabled={Boolean(busy)}>{busy ? 'Loading…' : 'Apply Filters'}</button>
        </div>
        </div>
      </form>

      {error && <div className="enterprise-card business-source-empty"><h3>Report could not be generated</h3><p>{error}</p><button className="btn btn-outline" onClick={() => void load()}>Try again</button></div>}

      {report && (
        <>
          {report.status === 'INACTIVE' && <div className="business-file-import-policy blocked"><strong>Activation intentionally pending</strong><p>{report.activation.message} Preview, validation and reconciliation remain available without publishing production figures.</p></div>}
          <div className="medics-revenue-section-heading">
            <div><h3>Revenue overview</h3><p>Governed MAIN and PHARMACY revenue, shown in lakhs.</p></div>
            <span className={`business-source-status ${report.status === 'READY' ? 'valid' : 'warning'}`}>{report.status === 'READY' ? 'Data Ready' : report.status.replace(/_/g, ' ')}</span>
          </div>
          <div className="daily-revenue-kpis medics-revenue-kpis">
            <RevenueSummary label="MAIN revenue" value={main?.amount || '0'} detail={`${(main?.rowCount || 0).toLocaleString('en-IN')} facts`} />
            <RevenueSummary label="PHARMACY revenue" value={pharmacy?.amount || '0'} detail={`${(pharmacy?.rowCount || 0).toLocaleString('en-IN')} facts`} />
            <RevenueSummary label="Combined revenue" value={report.totals.combined} detail={`${report.totals.rowCount.toLocaleString('en-IN')} facts`} />
            <RevenueSummary label="Reporting period" value={`${report.period.from} – ${report.period.to}`} detail={report.period.view.replace(/_/g, ' ')} text />
          </div>

          <div className="card enterprise-table-card medics-revenue-card">
            <div className="enterprise-card-heading"><div><strong>Centre-wise revenue</strong><span>Amounts are shown in lakhs; no patient or MRN fields are exposed.</span></div></div>
            <div className="enterprise-table-wrap">
              <table className="enterprise-table medics-revenue-table">
                <thead><tr><th>Centre</th><th>MAIN</th><th>PHARMACY</th><th>Combined</th><th>Rows</th></tr></thead>
                <tbody>
                  {centers.map((row) => <tr key={row.centerCode}><td><strong>{row.displayName}</strong><br /><span className="business-source-secondary">{row.centerCode}</span></td><td>{lakhs(row.main)}</td><td>{lakhs(row.pharmacy)}</td><td><strong>{lakhs(row.combined)}</strong></td><td>{row.rowCount.toLocaleString('en-IN')}</td></tr>)}
                  {!centers.length && <tr><td colSpan={5}>No confirmed facts exist for this period. The source remains inactive.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card enterprise-table-card medics-revenue-card">
            <div className="enterprise-card-heading"><div><strong>{view === 'MONTHLY_TREND' ? 'Monthly' : 'Daily'} trend</strong><span>Signed values and sales returns are reflected once in the totals.</span></div></div>
            <div className="enterprise-table-wrap">
              <table className="enterprise-table medics-revenue-table"><thead><tr><th>Period</th><th>MAIN</th><th>PHARMACY</th><th>Combined</th></tr></thead>
                <tbody>{trend.map((row) => <tr key={'date' in row ? row.date : row.month}><td>{'date' in row ? row.date : row.month}</td><td>{lakhs(row.main)}</td><td>{lakhs(row.pharmacy)}</td><td><strong>{lakhs(row.combined)}</strong></td></tr>)}{!trend.length && <tr><td colSpan={4}>No trend data is available.</td></tr>}</tbody>
              </table>
            </div>
          </div>

          <div className="card enterprise-table-card medics-revenue-card">
            <div className="enterprise-card-heading"><div><strong>Latest-batch reconciliation</strong><span>Restricted source rows are compared with facts created by the same batch; sensitive source columns are never returned. Exact repeat uploads are safely excluded.</span></div><span className={`business-source-status ${report.reconciliation?.status === 'RECONCILED' ? 'valid' : 'warning'}`}>{report.reconciliation?.status || 'No batch'}</span></div>
            {Boolean(report.reconciliation?.actionableChangeRowCount) && <p className="business-source-secondary"><strong>{report.reconciliation?.actionableChangeRowCount.toLocaleString('en-IN')} genuinely changed source rows require review in Validation &amp; Discrepancies.</strong></p>}
            <div className="enterprise-table-wrap"><table className="enterprise-table medics-revenue-table"><thead><tr><th>Category</th><th>New valid rows / amount</th><th>Created facts / amount</th><th>Difference</th></tr></thead><tbody>
              {(report.reconciliation?.categories ?? []).map((row) => <tr key={row.sourceCategory}><td><strong>{row.sourceCategory}</strong></td><td>{row.rawRowCount.toLocaleString('en-IN')} · {lakhs(row.rawAmount)}</td><td>{row.factRowCount.toLocaleString('en-IN')} · {lakhs(row.factAmount)}</td><td>{lakhs(row.difference)}</td></tr>)}
              {!report.reconciliation && <tr><td colSpan={4}>No completed import batch exists.</td></tr>}
            </tbody></table></div>
          </div>
        </>
      )}
      {alert && <AdminAlertModal {...alert} onClose={() => setAlert(null)} />}
    </section>
  );
}

function RevenueSummary({ label, value, detail, text = false }: { label: string; value: string; detail: string; text?: boolean }) {
  return <article className={`daily-revenue-kpi medics-revenue-kpi${text ? ' period' : ''}`}><span>{label}</span><strong>{text ? value : lakhs(value)}</strong><small>{detail}</small></article>;
}

function DownloadIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}

function lakhs(value: string) {
  const amount = Number(value);
  return Number.isFinite(amount) ? `${(amount / 100_000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L` : value;
}

function message(error: unknown) {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}
